import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, FileText, Save, Loader2, Eye, EyeOff } from "lucide-react";
import ImageTextarea, { ContentWithImages } from "@/components/ImageTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsentTextManagement({ user }) {
  const [consentTexts, setConsentTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedTexts, setEditedTexts] = useState({});

  useEffect(() => {
    fetchConsentTexts();
  }, []);

  const fetchConsentTexts = async () => {
    try {
      const response = await axios.get(`${API}/academy/admin/consent-texts`, {
        withCredentials: true,
      });
      setConsentTexts(response.data || []);
      // Initialize edited texts
      const edited = {};
      response.data.forEach((text) => {
        edited[text.id] = { ...text };
      });
      setEditedTexts(edited);
    } catch (error) {
      console.error("Error fetching consent texts:", error);
      toast.error("Failed to load consent texts");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (id, field, value) => {
    setEditedTexts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = async (id) => {
    setSaving(id);
    try {
      const text = editedTexts[id];
      await axios.post(
        `${API}/academy/admin/consent-texts/${id}`,
        {
          title: text.title,
          content: text.content,
          is_active: text.is_active,
          order: text.order,
        },
        { withCredentials: true }
      );
      toast.success("Consent text saved successfully!");
      fetchConsentTexts();
    } catch (error) {
      console.error("Error saving consent text:", error);
      toast.error("Failed to save consent text");
    } finally {
      setSaving(null);
    }
  };

  const hasChanges = (id) => {
    const original = consentTexts.find((t) => t.id === id);
    const edited = editedTexts[id];
    if (!original || !edited) return false;
    return (
      original.title !== edited.title ||
      original.content !== edited.content ||
      original.is_active !== edited.is_active ||
      original.order !== edited.order
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white">📋 Consent Text Management</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Card className="p-6 mb-6 bg-amber-500/10 border-amber-500/30">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-semibold text-amber-300">Edit Consent Forms</h2>
              <p className="text-sm text-amber-400 mt-1">
                These texts appear in the registration flow before payment. Parents must agree to all active consent forms before proceeding.
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          {consentTexts.map((text) => (
            <Card key={text.id} className={`p-6 ${!editedTexts[text.id]?.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Badge
                    className={
                      text.type === "pdpa"
                        ? "bg-blue-500/20 text-blue-400"
                        : text.type === "safe_sport"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-purple-500/20 text-purple-400"
                    }
                  >
                    {text.type.toUpperCase()}
                  </Badge>
                  <span className="text-sm text-slate-400">Order: {text.order}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {editedTexts[text.id]?.is_active ? (
                      <Eye className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-400">Active</span>
                    <Switch
                      checked={editedTexts[text.id]?.is_active || false}
                      onCheckedChange={(checked) =>
                        handleInputChange(text.id, "is_active", checked)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Title
                  </label>
                  <Input
                    value={editedTexts[text.id]?.title || ""}
                    onChange={(e) =>
                      handleInputChange(text.id, "title", e.target.value)
                    }
                    placeholder="Consent form title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Content
                  </label>
                  <ImageTextarea
                    value={editedTexts[text.id]?.content || ""}
                    onChange={(value) =>
                      handleInputChange(text.id, "content", value)
                    }
                    placeholder="Consent form content... You can insert images at any position."
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Use bullet points (•) for lists. Line breaks will be preserved. Click "Insert Image" to add images inline.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-slate-400">
                    Last updated: {new Date(text.updated_at).toLocaleString()}
                  </div>
                  <Button
                    onClick={() => handleSave(text.id)}
                    disabled={!hasChanges(text.id) || saving === text.id}
                    className={hasChanges(text.id) ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {saving === text.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {hasChanges(text.id) ? "Save Changes" : "No Changes"}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Preview Section */}
        <Card className="p-6 mt-8 bg-[#0a0f1a]">
          <h3 className="font-semibold mb-4">📱 Preview (as seen by parents)</h3>
          <div className="space-y-4 max-w-2xl">
            {consentTexts
              .filter((t) => editedTexts[t.id]?.is_active)
              .sort((a, b) => a.order - b.order)
              .map((text) => (
                <div key={text.id} className="p-4 bg-slate-900/50 rounded-lg border">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 border-2 border-slate-700 rounded mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-white">
                        {editedTexts[text.id]?.title}
                      </p>
                      <div className="mt-2 text-sm text-slate-400 bg-[#0a0f1a] p-3 rounded max-h-24 overflow-y-auto whitespace-pre-wrap">
                        {editedTexts[text.id]?.content?.substring(0, 300)}
                        {editedTexts[text.id]?.content?.length > 300 && "..."}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
